import React from "react";
import { useTranslation } from "react-i18next";
import { TournamentMatrix, rankMatrixRows } from "../../types/tournament";
import { ChevronRight } from "lucide-react";
import Avatar from "../common/Avatar";

interface Props {
  matrix: TournamentMatrix;
  currentUserId?: string;
  totalGames?: number;
  currentGame?: number;
  onMatchClick?: (sessionId: string) => void;
}

const TournamentStandings: React.FC<Props> = ({ matrix, currentUserId, totalGames, currentGame, onMatchClick }) => {
  const { t } = useTranslation();

  // Rank by the tournament's winner mode; the other metric breaks ties
  const mode = matrix.winner_mode ?? "points";
  const sortedRows = rankMatrixRows(matrix.matrix, mode);

  // Per-game winner: max score in each game column
  const perGameMax = matrix.games.map((_, i) => {
    const scores = sortedRows.map(r => r.scores[i]).filter((s): s is number => s !== null);
    return scores.length > 0 ? Math.max(...scores) : null;
  });

  return (
    <div className="flex flex-col space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {totalGames !== undefined && currentGame !== undefined && (
        <div className="flex items-center justify-between px-2">
           <div className="text-[10px] font-black text-ink/40 uppercase tracking-[0.2em]">
             {t("tournament.progress", "Progress")}
           </div>
           <div className="bg-yellow-500/20 text-yellow-500 px-3 py-1 rounded-full text-xs font-black border border-yellow-500/30">
             {currentGame}/{totalGames}
           </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl bg-ink/5 border border-ink/10 backdrop-blur-md shadow-xl">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-ink/10 bg-ink/5">
              <th className="px-4 py-4 text-xs font-bold text-ink/50 uppercase tracking-widest sticky left-0 bg-surface">
                {t("stats.player")}
              </th>
              {matrix.games.map((_, i) => (
                <th key={i} className="px-4 py-4 text-xs font-bold text-ink/50 uppercase tracking-widest text-center whitespace-nowrap">
                  {t("tournament.game")} {i + 1}
                </th>
              ))}
              <th className={`px-4 py-4 text-xs font-bold uppercase tracking-widest text-center ${
                mode === "wins" ? "text-yellow-500" : "text-ink/50"
              }`}>
                {t("tournament.winsColumn")}
              </th>
              <th className={`px-4 py-4 text-xs font-bold uppercase tracking-widest text-right ${
                mode === "points" ? "text-yellow-500" : "text-ink/50"
              }`}>
                {t("tournament.grandTotal")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, index) => {
              const isMe = row.player_id === currentUserId;
              return (
                <tr 
                  key={row.player_id}
                  className={`border-b border-ink/5 transition-colors ${
                    isMe ? "bg-yellow-500/10" : "hover:bg-ink/5"
                  }`}
                >
                  <td className={`px-4 py-4 font-bold sticky left-0 bg-surface z-10 ${
                    isMe ? "text-yellow-400" : "text-ink"
                  }`}>
                    <div className="flex items-center space-x-2">
                      <span className="text-ink/30 text-xs w-4">{index + 1}</span>
                      <Avatar src={row.avatar ?? null} size="sm" />
                      <span className="truncate max-w-[100px]">{row.display_name}</span>
                    </div>
                  </td>
                  {row.scores.map((score, i) => {
                    const isWinner = score !== null && perGameMax[i] !== null && score === perGameMax[i];
                    return (
                      <td key={i} className="px-4 py-4 text-center">
                        <span className={
                          score === null
                            ? "text-ink/10"
                            : isWinner
                              ? "text-yellow-400 font-black font-mono"
                              : "text-ink/80 font-mono"
                        }>
                          {score ?? "—"}
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-4 py-4 text-center">
                    <span className={`font-bold font-mono text-lg ${
                      mode === "wins" ? "text-yellow-500" : "text-ink/70"
                    }`}>
                      {row.wins}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className={`font-bold font-mono text-lg ${
                      mode === "points" ? "text-yellow-500" : "text-ink/70"
                    }`}>
                      {row.grand_total}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {matrix.games.length === 0 && (
        <div className="px-2 py-4 flex flex-wrap gap-2">
          <div className="bg-ink/5 rounded-full px-4 py-2 border border-ink/10 flex items-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse" />
            <span className="text-xs text-ink/60 font-medium uppercase tracking-wider">
              {t("tournament.waitingParticipants")}
            </span>
          </div>
        </div>
      )}

      {/* List of games */}
      {matrix.games.length > 0 && (
        <div className="space-y-3 px-2 pt-4">
          <h3 className="text-[10px] font-black text-ink/40 uppercase tracking-[0.2em] mb-2">
            {t("tournament.rounds")}
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {matrix.games.map((gid, i) => (
              <button
                key={gid}
                onClick={() => onMatchClick?.(gid)}
                className="w-full flex items-center justify-between p-4 bg-ink/5 hover:bg-ink/10 border border-ink/10 rounded-2xl transition-all group"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-black border border-blue-500/30">
                    {i + 1}
                  </div>
                  <span className="font-bold text-ink group-hover:text-blue-400 transition-colors">
                    {t("tournament.game")} {i + 1}
                  </span>
                </div>
                <ChevronRight className="w-5 h-5 text-ink/20 group-hover:text-ink transition-colors" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentStandings;
